import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigFacadeService } from '@core/services/config/config-facade.service';
import { handleError } from '@utils/helpers';
import { makeUri } from '@utils/string.tool';
import { Observable, of, Subject, timer } from 'rxjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { PartnerAccountToOnboard } from '../../interfaces/partner-links.interface';
import { IPartnerStatusResp, IPartnerValidateArnResp } from '../../interfaces/partner-portal.interface';

export interface IProgressBar {
	value: number;
	accountsTotal: number;
	accountsReady: number;
}

export const notReady1: IPartnerStatusResp = {
	message: 'Not ready yet',
	task: {
		accounts: {
			'42f7eb58-617e-11eb-a55b-4ab2dc7975ee': {
				account_alias: 'My account 1',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
			'd407270a-620d-11eb-99fe-3632e85192ed': {
				account_alias: 'Wave Radio Reseller-1',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'sent in queue',
			},
			'd41fadc0-620d-11eb-99fe-3632e85192ed': {
				account_alias: 'Wave Radio Reseller-2',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'sent in queue',
			},
			'd42be414-620d-11eb-99fe-3632e85192ed': {
				account_alias: '"Wave Radio Reseller-3"',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'sent in queue',
			},
		},
		accounts_count: 3,
		accounts_total_count: 4,
		created: '2021-01-24T13:45:27+0000',
		organizations_list: ['organizations_list'],
		property: 'reseller_onboarding_task_id',
		reseller_id: 'reseller_id',
	},
};

export const notReady3: IPartnerStatusResp = {
	message: 'Not ready yet',
	task: {
		accounts: {
			'42f7eb58-617e-11eb-a55b-4ab2dc7975ee': {
				account_alias: 'My account 1',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
			'd407270a-620d-11eb-99fe-3632e85192ed': {
				account_alias: 'Wave Radio Reseller-1',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'sent in queue',
			},
			'd41fadc0-620d-11eb-99fe-3632e85192ed': {
				account_alias: 'Wave Radio Reseller-2',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
			'd42be414-620d-11eb-99fe-3632e85192ed': {
				account_alias: '"Wave Radio Reseller-3"',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'sent in queue',
			},
		},
		accounts_count: 2,
		accounts_total_count: 4,
		created: '2021-01-24T13:45:27+0000',
		organizations_list: ['organizations_list'],
		property: 'reseller_onboarding_task_id',
		reseller_id: 'reseller_id',
	},
};

export const ReadyAll: IPartnerStatusResp = {
	message: 'Ready',
	task: {
		accounts: {
			'42f7eb58-617e-11eb-a55b-4ab2dc7975ee': {
				account_alias: 'My account 1',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
			'd407270a-620d-11eb-99fe-3632e85192ed': {
				account_alias: 'Wave Radio Reseller-1',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
			'd41fadc0-620d-11eb-99fe-3632e85192ed': {
				account_alias: 'Wave Radio Reseller-2',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
			'd42be414-620d-11eb-99fe-3632e85192ed': {
				account_alias: '"Wave Radio Reseller-3"',
				aws_account_id: 'aws_account_id',
				onboarding_status: 'onboarded',
			},
		},
		accounts_count: 0,
		accounts_total_count: 4,
		created: '2021-01-24T13:45:27+0000',
		organizations_list: ['organizations_list'],
		property: 'reseller_onboarding_task_id',
		reseller_id: 'reseller_id',
	},
};

@Injectable({ providedIn: 'root' })
export class PartnerOnboardingService {
	onboardingInProgress$ = new BehaviorSubject<boolean>(false);
	onboardingSuccess$ = new BehaviorSubject<boolean>(false);
	progressBar: IProgressBar = {
		value: 0,
		accountsTotal: 0,
		accountsReady: 0,
	};
	accountsToOnboard: PartnerAccountToOnboard[];
	curBucketName: string = '';
	onboardedAccounts = new BehaviorSubject<PartnerAccountToOnboard[]>([]);
	stopPolling$ = new Subject();
	errorMessage = '';

	showMinifiedProgress$ = new BehaviorSubject(false);

	constructor(private http: HttpClient, private appConfig: ConfigFacadeService) {}

	checkArnValidity() {
		this.validateARN(this.accountsToOnboard, this.curBucketName)
			.pipe(catchError((err) => handleError(err, null)))
			.subscribe({
				next: (resp) => {
					if (resp.message === 'Onboarding task is created') {
						this.errorMessage = null;
						this.startOnboardingPolling(resp.reseller_id, resp.task_id);
					} else {
						this.errorMessage = resp.message;
						return;
					}
				},
			});
	}

	startOnboardingPolling(resellerId, taskId) {
		// let requestCount = 0;
		timer(6000, 6000)
			.pipe(
				mergeMap((_) => this.validateResellerStatus(resellerId, taskId)),
				// mergeMap(_ => this.simulateOnboarding(requestCount)),
				takeUntil(this.stopPolling$),
				catchError((err) => {
					this.stopPolling$.next();
					this.onboardingInProgress$.next(false);
					this.clearProgress();
					this.errorMessage = err.message;
					return of({} as IPartnerStatusResp);
				}),
			)
			.subscribe({
				next: (resp) => {
					// requestCount++;
					switch (resp.message) {
						case 'Ready':
							this.stopPolling$.next();
							this.onboardingSuccess$.next(true);
							this.calculateProgress(resp.task);
							const onboardedAccounts = [];

							this.accountsToOnboard.forEach((account) => {
								const accountsMap = resp.task.accounts;
								const accountUuid = Object.keys(accountsMap).find((uuid) => uuid === account.account_uuid);
								const accountToOnboard = accountsMap[accountUuid];
								if (accountToOnboard && accountToOnboard.onboarding_status === 'onboarded') {
									account.isOnboarded = true;
								}
								onboardedAccounts.push(account);
							});
							this.onboardedAccounts.next(onboardedAccounts);
							this.onboardingSuccess();
							break;
						case 'Task not found':
							this.stopPolling$.next();
							this.onboardingInProgress$.next(false);
							break;
						case 'Not ready yet':
							this.calculateProgress(resp.task);
					}
				},
			});
	}

	onboardingSuccess() {
		if (this.showMinifiedProgress$.value) {
			// if modal is hidden
			this.onboardingInProgress$.next(false);
		} else {
			timer(1000).subscribe((_) => {
				this.onboardingSuccess$.next(false);
				this.onboardingInProgress$.next(false);
				this.clearProgress();
			});
		}
	}

	clearProgress() {
		this.progressBar.value = 0;
		this.progressBar.accountsReady = 0;
		this.progressBar.accountsTotal = 0;
	}

	calculateProgress(task) {
		const accountsTotal = task.accounts_total_count;
		const accountsLeft = task.accounts_count;
		if (accountsLeft === null || accountsLeft === undefined) {
			this.progressBar.accountsReady = 0;
		} else if (accountsLeft === 0) {
			this.progressBar.value = 100;
			this.progressBar.accountsReady = accountsTotal;
		} else {
			this.progressBar.accountsTotal = accountsTotal;
			this.progressBar.accountsReady = accountsTotal - accountsLeft;
			this.progressBar.value = ((accountsTotal - accountsLeft) / accountsTotal) * 100;
		}
	}

	/**
	 * Creates onboarding task on BE
	 * @param data - array of accounts to onboard
	 */
	validateARN(data: any[], cur_bucket_name: string): Observable<IPartnerValidateArnResp> {
		const url = makeUri(this.appConfig.config.apiUrl, 'onboard/reseller_validate_arn');
		return this.http.post<IPartnerValidateArnResp>(url, { data, cur_bucket_name });
	}

	/**
	 * Polling for onboarding task completion
	 * @param {string} resellerId
	 * @param {string} taskId
	 */
	validateResellerStatus(resellerId, taskId): Observable<IPartnerStatusResp> {
		const url = makeUri(this.appConfig.config.apiUrl, 'onboard/validate_reseller_status');
		return this.http
			.post<IPartnerStatusResp>(url, {
				reseller_id: resellerId,
				task_id: taskId,
			})
			.pipe(catchError((err) => handleError(err, null)));
	}

	simulateOnboarding(count): Observable<IPartnerStatusResp> {
		switch (count) {
			case 0:
				return of(notReady1);
			case 1:
				return of(notReady3);
			case 2:
				return of(ReadyAll);
			default:
				return of({} as IPartnerStatusResp);
		}
	}
}
